// ELO: the competitive score (0-4200) that sets your rank (Bronze → Master) and orders the leaderboard.
// Separate from player level / XP. Stored on the profile as `elo` (+ `eloStreak`, `eloLast`).
//
// Per match (before the mode multiplier):
//   +1 per splat, -1 per time splatted
//   +5 for a win, -5 for a loss (a draw is 0)
//   +1 per 400p of turf inked, up to +3        (objective play counts, not just fragging)
//   +2 for finishing top of your team           (MVP)
//   +1 per win in a row after the first, up to +3
//   the total is held to -15 … +20               (one bad or lucky game can't swing it wildly)
// Mode multiplier: vs bots ×1 · online ×2 · ranked ×10. ELO never drops below 0 or passes 4200.

export const ELO_MAX = 4200;
export const ELO_MULT = { bots: 1, online: 2, ranked: 10 };

// tier floors (the same six tiers as the rank emblems: Bronze → Master), each split into divisions I · II · III
export const ELO_TIERS = [
  { elo: 0, name: 'Bronze' },
  { elo: 600, name: 'Silver' },
  { elo: 1200, name: 'Gold' },
  { elo: 1800, name: 'Platinum' },
  { elo: 2500, name: 'Diamond' },
  { elo: 3300, name: 'Master' },
];

const clampElo = (v) => Math.max(0, Math.min(ELO_MAX, Math.round(+v || 0)));

/** Tier index 0..5 for an ELO score. */
export function eloTier(elo) {
  const e = clampElo(elo);
  let t = 0;
  for (let i = 0; i < ELO_TIERS.length; i++) if (e >= ELO_TIERS[i].elo) t = i;
  return t;
}

/** { tier, name, div ('I'|'II'|'III'), label ('GOLD II'), next (tier or null), toNext (ELO to the next tier) } */
export function eloRank(elo) {
  const e = clampElo(elo), tier = eloTier(e), cur = ELO_TIERS[tier], next = ELO_TIERS[tier + 1] || null;
  const top = next ? next.elo : ELO_MAX + 1;
  const div = ['I', 'II', 'III'][Math.min(2, Math.floor(((e - cur.elo) / (top - cur.elo)) * 3))];
  return { tier, name: cur.name, div, label: `${cur.name.toUpperCase()} ${div}`, next, toNext: next ? next.elo - e : 0 };
}

/** Your ELO, starting a profile that predates ELO from its lifetime record (at the vs-bots rate). */
export function profileElo(p) {
  if (!p) return 0;
  if (Number.isFinite(p.elo)) return clampElo(p.elo);
  const wins = p.wins || 0, losses = Math.max(0, (p.matches || 0) - wins);
  return clampElo((p.kills || 0) - (p.deaths || 0) + 5 * (wins - losses));
}

/** 'bots' | 'online' | 'ranked' for the match that just ended. */
export function matchKind({ online = false, ranked = false } = {}) {
  return ranked ? 'ranked' : online ? 'online' : 'bots';
}

/**
 * The ELO change for one match. `r` = { won, draw, splats, deaths, turf, mvp, kind }.
 * `streak` = wins in a row before this match. Returns { delta, base, parts: [[label, n]…], mult }.
 */
export function eloDelta(r, streak = 0) {
  const parts = [];
  const add = (label, n) => { if (n) parts.push([label, n]); };
  add('SPLATS', r.splats | 0);
  add('SPLATTED', -(r.deaths | 0));
  add(r.won ? 'WIN' : 'LOSS', r.draw ? 0 : r.won ? 5 : -5);
  add('TURF', Math.min(3, Math.floor((r.turf || 0) / 400)));
  if (r.mvp) add('MVP', 2);
  if (r.won && !r.draw) add('STREAK', Math.min(3, Math.max(0, streak | 0)));
  const base = Math.max(-15, Math.min(20, parts.reduce((s, [, n]) => s + n, 0)));
  const mult = ELO_MULT[r.kind] || 1;
  return { delta: base * mult, base, parts, mult };
}

/**
 * Apply one match to the profile (mutates `p`): elo, eloStreak, eloLast. Returns { before, after, delta, … }.
 */
export function applyMatch(p, r) {
  const before = profileElo(p);
  const streak = p.eloStreak | 0;
  const d = eloDelta(r, streak);
  const after = clampElo(before + d.delta);
  p.elo = after;
  p.eloStreak = r.won && !r.draw ? streak + 1 : 0;
  p.eloLast = { before, after, delta: after - before, kind: r.kind, at: Date.now() };
  return { before, after, ...d, delta: after - before, rankBefore: eloRank(before), rankAfter: eloRank(after) };
}

// ---------------------------------------------------------------- leaderboard bots
// Small seeded RNG so each named player keeps the same record every visit (and between players).
function rng(seedStr) {
  let h = 2166136261;
  for (let i = 0; i < seedStr.length; i++) { h ^= seedStr.charCodeAt(i); h = Math.imul(h, 16777619); }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A spread-out record for a named leaderboard player: ELO across all six tiers (most in Bronze–Gold, a few Masters),
 * with wins / losses / splats / deaths that follow from it plus some noise. Level is rolled separately.
 */
export function rollRecord(name, season = 's1') {
  const r = rng(`${season}:${name}`);
  const elo = clampElo(ELO_MAX * Math.pow(r(), 1.55));
  const s = elo / ELO_MAX;
  const matches = Math.round(30 + r() * 420 + s * 520);
  const winRate = Math.max(0.2, Math.min(0.86, 0.36 + s * 0.38 + (r() - 0.5) * 0.14));
  const wins = Math.round(matches * winRate);
  const kpm = 2 + s * 5.5 + r() * 2.5, dpm = 5.8 - s * 2.6 + r() * 2.2;
  return {
    elo,
    wins, losses: matches - wins,
    kills: Math.round(matches * kpm), deaths: Math.round(matches * dpm),
    level: Math.max(1, Math.min(99, Math.round(matches / 14 + r() * 12))),
  };
}
